import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSpaceStore } from '@/stores/spaceStore'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import styles from './TopBar.module.css'

const FIRST_LEVEL_PAGES: Record<string, string> = {
  '/': '日历',
  '/data': '数据',
  '/messages': '消息',
  '/events': '事件',
  '/profile': '我的',
}

const SECOND_LEVEL_TITLES: Record<string, string> = {
  '/vault': '私密空间',
  '/period': '生理期',
  '/spaces': '空间列表',
  '/space-manage': '管理空间',
  '/report': '月报',
}

interface Props {
  onMenuClick: () => void
}

export default function TopBar({ onMenuClick }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeSpace, currentSpace } = useSpaceStore()
  const userInfo = useAuthStore(s => s.userInfo)
  const { isDark, toggleDark, subPageTitle } = useUiStore()

  const pathname = location.pathname
  const isFirstLevel = !!FIRST_LEVEL_PAGES[pathname]
  const isSecondLevel = !isFirstLevel

  const getSpaceLabel = () => {
    if (activeSpace === 'vault') return '🔒 私密'
    if (activeSpace === 'period') return '🌸 生理期'
    if (activeSpace.startsWith('space:') && currentSpace) {
      const icon = currentSpace.icon || (currentSpace.type === 'couple' ? '💕' : '👥')
      return icon + ' ' + currentSpace.name
    }
    return '📅 ' + (userInfo?.spaceName || '默认空间')
  }

  let pageTitle = ''
  if (pathname.startsWith('/space/')) {
    pageTitle = currentSpace?.name || '空间详情'
  } else {
    pageTitle = SECOND_LEVEL_TITLES[pathname] || '日迹'
  }

  useEffect(() => {
    if (isFirstLevel) {
      document.title = FIRST_LEVEL_PAGES[pathname] + ' | ' + getSpaceLabel()
    } else {
      document.title = pageTitle
    }
  }, [pathname, activeSpace, currentSpace, userInfo])

  // 子页面（ProfilePage子页面等）
  if (subPageTitle) {
    return (
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => {
          useUiStore.getState().setSubPageTitle(null)
          navigate('/profile')
        }}>←</button>
        <span className={styles.title}>{subPageTitle}</span>
        <div className={styles.actions}>
          <button className={styles.themeBtn} onClick={toggleDark}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </div>
    )
  }

  // 二级页面
  if (isSecondLevel) {
    return (
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>←</button>
        <span className={styles.title}>{pageTitle}</span>
        <div className={styles.actions}>
          <button className={styles.themeBtn} onClick={toggleDark}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </div>
    )
  }

  // 一级页面
  const title = FIRST_LEVEL_PAGES[pathname] + ' | ' + getSpaceLabel()
  return (
    <div className={styles.topBar}>
      <button className={styles.menuBtn} onClick={onMenuClick}>☰</button>
      <span className={styles.title}>{title}</span>
      <div className={styles.actions}>
        <button className={styles.themeBtn} onClick={toggleDark}>
          {isDark ? '☀️' : '🌙'}
        </button>
      </div>
    </div>
  )
}
